/**
 * A single, low-key "suggested next" nudge for the Home screen.
 *
 * Built entirely from data the app already stores (mastery, when a topic was
 * last practiced, recent sessions, weak subskills). It is intentionally quiet:
 * it offers at most one suggestion and stays silent when there is nothing worth
 * saying, so it never nags. There are no notifications and no backend - this is
 * a pure function of the inputs, so it is deterministic and unit-testable.
 */

import type { TopicId } from "../curriculum/curriculum";
import { getTopic } from "../curriculum/curriculum";
import type { MasteryState } from "../progress/model";

/** What a suggestion asks the student to do. */
export type SuggestionAction =
  | { kind: "practice"; topic: TopicId }
  | { kind: "learn"; topic: TopicId }
  | { kind: "recommended" };

export interface Suggestion {
  readonly message: string;
  readonly cta: string;
  readonly action: SuggestionAction;
}

/** Per-topic facts the suggester reasons over. */
export interface TopicStatus {
  readonly topic: TopicId;
  readonly mastery: MasteryState;
  readonly lastPracticedAt: number | null;
}

export interface SuggestInput {
  readonly now: number;
  /** Only taught, generator-backed topics, in teaching order. */
  readonly topics: readonly TopicStatus[];
  /** Number of weak subskills currently flagged. */
  readonly concernCount: number;
  /** Total sessions recorded (to detect a brand-new user). */
  readonly sessionCount: number;
}

const STALE_MS = 7 * 24 * 60 * 60 * 1000; // a week without review

/**
 * Choose the single most useful nudge, or null if nothing stands out. Priority:
 *  1. Brand-new user -> point at the first topic's lesson.
 *  2. A topic is close to mastery (proficient) -> encourage finishing it.
 *  3. There are weak subskills -> offer the recommended review.
 *  4. A previously-practiced topic has gone stale (>1 week) -> suggest a refresh.
 * Otherwise stay silent.
 */
export function suggestNext(input: SuggestInput): Suggestion | null {
  const { topics, now } = input;
  if (topics.length === 0) return null;

  // 1. Nothing practiced yet.
  if (input.sessionCount === 0) {
    const first = topics[0]!;
    return {
      message: `New here? Start with a quick lesson on ${getTopic(first.topic).name}.`,
      cta: "Open lesson",
      action: { kind: "learn", topic: first.topic },
    };
  }

  // 2. Close to mastering something (proficient but not yet strong).
  const almost = topics.find((t) => t.mastery === "proficient");
  if (almost) {
    return {
      message: `You're close to mastering ${getTopic(almost.topic).name}. A few more could push it to Strong.`,
      cta: "Practice it",
      action: { kind: "practice", topic: almost.topic },
    };
  }

  // 3. Weak subskills worth a targeted review.
  if (input.concernCount > 0) {
    return {
      message: `A few skills have been slipping lately. A short mixed review could help.`,
      cta: "Recommended review",
      action: { kind: "recommended" },
    };
  }

  // 4. A practiced topic has gone stale.
  const stale = topics
    .filter((t) => t.lastPracticedAt !== null && now - t.lastPracticedAt > STALE_MS)
    .sort((a, b) => (a.lastPracticedAt ?? 0) - (b.lastPracticedAt ?? 0))[0];
  if (stale) {
    const days = Math.floor((now - (stale.lastPracticedAt ?? now)) / (24 * 60 * 60 * 1000));
    return {
      message: `You haven't reviewed ${getTopic(stale.topic).name} in ${days} days.`,
      cta: "Refresh it",
      action: { kind: "practice", topic: stale.topic },
    };
  }

  return null;
}
